import React, { useEffect, useMemo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useLocation, useNavigate } from "react-router-dom";
import allApi from "../../../../api/allApi";
import { useLocalization } from "../../localization/LocalizationContext.js";
import { useNetwork } from "../../common/networkContext.js";
import { useSidebar } from "../../common/sidebarContext.js";
import NmsWidget from "./NmsWidget";
import OverviewTrends from "./OverviewTrends";
import OverviewSystemHealth from "./OverviewSystemHealth";
import {
  AlertsChart,
  ClientsChart,
  DeviceHealthChart,
  DeviceInventory,
  EdgesPanel,
  OverviewKpiStrip,
} from "./overviewWidgets";
import "../../../../assets/css/overview-grid.css";

const ResponsiveGridLayout = WidthProvider(Responsive);
const LAYOUT_KEY = "inc-overview-layout-v2";

const baseLayout = [
  { i: "devices", x: 0, y: 0, w: 4, h: 7, minW: 3, minH: 5 },
  { i: "alerts", x: 4, y: 0, w: 4, h: 7, minW: 3, minH: 5 },
  { i: "clients", x: 8, y: 0, w: 4, h: 7, minW: 3, minH: 5 },
  { i: "edges", x: 0, y: 7, w: 4, h: 6, minW: 3, minH: 4 },
  { i: "inventory", x: 4, y: 7, w: 8, h: 6, minW: 4, minH: 4 },
  { i: "trends", x: 0, y: 13, w: 8, h: 9, minW: 5, minH: 6 },
  { i: "health", x: 8, y: 13, w: 4, h: 9, minW: 3, minH: 6 },
];

const stackedLayout = (cols) => [
  { i: "devices", x: 0, y: 0, w: cols, h: 7, minW: 3, minH: 5 },
  { i: "alerts", x: 0, y: 7, w: cols, h: 7, minW: 3, minH: 5 },
  { i: "clients", x: 0, y: 14, w: cols, h: 7, minW: 3, minH: 5 },
  { i: "edges", x: 0, y: 21, w: cols, h: 5, minW: 3, minH: 4 },
  { i: "inventory", x: 0, y: 26, w: cols, h: 8, minW: 3, minH: 4 },
  { i: "trends", x: 0, y: 34, w: cols, h: 9, minW: 3, minH: 6 },
  { i: "health", x: 0, y: 43, w: cols, h: 9, minW: 3, minH: 6 },
];

const defaultLayouts = {
  lg: baseLayout,
  md: baseLayout.map((item) => ({ ...item })),
  sm: stackedLayout(6),
  xs: stackedLayout(4),
};

const loadLayouts = (includeHealth) => {
  try {
    const stored = localStorage.getItem(LAYOUT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return filterHealth(parsed, includeHealth);
    }
  } catch (error) {
    console.error("Failed to load overview layout:", error);
  }
  return filterHealth(defaultLayouts, includeHealth);
};

const filterHealth = (layouts, includeHealth) =>
  Object.fromEntries(
    Object.entries(layouts).map(([breakpoint, items]) => [
      breakpoint,
      includeHealth ? items : items.filter((item) => item.i !== "health"),
    ])
  );

const OverviewDashboard = () => {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const { isCollapsed, isMobileNavOpen } = useSidebar();
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth > 768 : true
  );
  const networkId = selectedNetworkId?.id ?? 6001;
  const navigate = useNavigate();
  const location = useLocation();
  const license = localStorage.getItem("license") || "";
  const showHealth = Boolean(license && license !== "Standard");

  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [faults, setFaults] = useState([]);
  const [edgeCounts, setEdgeCounts] = useState({});
  const [wiredCount, setWiredCount] = useState(0);
  const [wirelessCount, setWirelessCount] = useState(0);
  const [layouts, setLayouts] = useState(() => loadLayouts(showHealth));

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [isCollapsed, isMobileNavOpen]);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      const api = new allApi();
      try {
        const [deviceRes, faultRes, edgeRes, clientRes] = await Promise.all([
          api.getRequest(`api/devices/network?networkId=${networkId}`).catch(() => []),
          api
            .getRequest(`api/alarms/overview?licenseType=${license}&networkId=${networkId}`)
            .catch(() => []),
          api.getRequest("api/gateway/edge_info").catch(() => ({})),
          api.getRequest(`api/client/connected?networkId=${networkId}`).catch(() => ({})),
        ]);

        const healthFilter = new URLSearchParams(location.search).get("health") || "All";
        const deviceList = Array.isArray(deviceRes) ? deviceRes : [];
        setDevices(
          healthFilter === "All"
            ? deviceList
            : deviceList.filter((device) => device.health === healthFilter)
        );
        setFaults(Array.isArray(faultRes) ? faultRes : []);
        setEdgeCounts({
          activeCount: edgeRes?.activeCount || 0,
          inactiveCount: edgeRes?.inactiveCount || 0,
          managedByGateway: edgeRes?.managedByGateway || 0,
          totalCount: (edgeRes?.activeCount || 0) + (edgeRes?.inactiveCount || 0),
        });
        setWiredCount(clientRes?.wiredClients?.length || 0);
        setWirelessCount(clientRes?.wirelessClients?.length || 0);
      } catch (error) {
        console.error("Error loading overview:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [networkId, location.search, license]);

  const deviceStats = useMemo(() => {
    const onlineCount = devices.filter((device) => device.health === "Online").length;
    const offlineCount = devices.filter((device) => device.health === "Offline").length;
    const awaitingCount = devices.filter((device) => device.health === "Awaiting").length;
    return {
      onlineCount,
      offlineCount,
      awaitingCount,
      totalDevices: devices.length,
    };
  }, [devices]);

  const alertTotal = Array.isArray(faults)
    ? faults.reduce((total, item) => total + (item.count || 0), 0)
    : 0;

  const fetchDeviceByStatus = async (statuses) => {
    try {
      const api = new allApi();
      const statusQueries = statuses.map((status) => `status=${status}`).join("&");
      const response = await api.getRequest(
        `api/devices/v2/status?${statusQueries}&networkId=${networkId}`
      );
      if (response) {
        navigate("/device-listing", { state: { deviceStatus: response } });
      }
    } catch (error) {
      console.error("Error fetching device", error);
    }
  };

  const fetchAlertsBySeverity = async (severityLevel) => {
    const severityLevelMap = { CRITICAL: 1, MAJOR: 2, MINOR: 3, INFO: 4 };
    const severityValue = severityLevelMap[severityLevel];
    if (!severityValue) return;
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/alarms/severity/${severityValue}?licenseType=${license}`
      );
      if (Array.isArray(response)) {
        navigate("/alarms", { state: { severityAlerts: response, fromOverview: true } });
      }
    } catch (error) {
      console.error("Error fetching severity alarms", error);
    }
  };

  const handleEdgeCountClick = async (status) => {
    try {
      const api = new allApi();
      const statusResponse = await api.getRequest(`api/gateway/edges/status?status=${status}`);
      if (statusResponse) {
        navigate("/edge-management", { state: { statusResponse } });
      }
    } catch (error) {
      console.error("Error fetching edge status data:", error);
    }
  };

  const handleEdgeManagedCountClick = async () => {
    try {
      const api = new allApi();
      const managedDevicesResponse = await api.getRequest(
        "api/devices/managedDevices?managedDevices=true"
      );
      navigate("/device-listing", { state: { deviceStatus: managedDevicesResponse } });
    } catch (error) {
      console.error("Error fetching managed devices:", error);
    }
  };

  const formatSeverityLevel = (severityLevel) => {
    switch (severityLevel) {
      case "CRITICAL":
        return localizationStrings?.critical || "Critical";
      case "MAJOR":
        return localizationStrings?.major || "Major";
      case "MINOR":
        return localizationStrings?.minor || "Minor";
      case "INFO":
        return localizationStrings?.info || "Info";
      default:
        return severityLevel;
    }
  };

  const handleLayoutChange = (_current, allLayouts) => {
    setLayouts(allLayouts);
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(allLayouts));
  };

  const resetLayout = () => {
    const next = filterHealth(defaultLayouts, showHealth);
    setLayouts(next);
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(next));
  };

  const deviceChartData = [
    { name: localizationStrings?.online || "Online", value: deviceStats.onlineCount, type: "ONLINE" },
    { name: localizationStrings?.offline || "Offline", value: deviceStats.offlineCount, type: "OFFLINE" },
    {
      name: localizationStrings?.awaiting || "Awaiting",
      value: deviceStats.awaitingCount,
      type: "AWAITING",
    },
  ];

  return (
    <div className="nms-overview">
      <OverviewKpiStrip
        items={[
          {
            label: localizationStrings?.devices || "Devices",
            value: deviceStats.totalDevices,
            onClick: () => navigate("/device-listing"),
          },
          {
            label: localizationStrings?.online || "Online",
            value: deviceStats.onlineCount,
            tone: "success",
            onClick: () => fetchDeviceByStatus(["ONLINE"]),
          },
          {
            label: localizationStrings?.offline || "Offline",
            value: deviceStats.offlineCount,
            tone: "danger",
            onClick: () => fetchDeviceByStatus(["OFFLINE"]),
          },
          {
            label: localizationStrings?.alerts || "Alerts",
            value: alertTotal,
            tone: alertTotal ? "warning" : "",
            onClick: () => navigate("/alarms"),
          },
          {
            label: localizationStrings?.clients || "Clients",
            value: wiredCount + wirelessCount,
            onClick: () => navigate("/client"),
          },
          {
            label: localizationStrings?.edges || "Edges",
            value: edgeCounts.totalCount || 0,
            onClick: () => navigate("/edge-management"),
          },
        ]}
      />

      <div className="nms-overview-toolbar">
        <button type="button" className="nms-reset-layout" onClick={resetLayout}>
          Reset layout
        </button>
      </div>

      <ResponsiveGridLayout
        className="nms-overview-grid"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
        cols={{ lg: 12, md: 12, sm: 6, xs: 4 }}
        rowHeight={28}
        margin={[14, 14]}
        containerPadding={[0, 0]}
        draggableHandle=".nms-widget-header"
        isDraggable={isDesktop}
        isResizable={isDesktop}
        onLayoutChange={handleLayoutChange}
        useCSSTransforms
        compactType="vertical"
        preventCollision={false}
      >
        <div key="devices">
          <NmsWidget
            title={localizationStrings?.devices || "Devices"}
            count={deviceStats.totalDevices}
          >
            <DeviceHealthChart
              loading={loading}
              data={deviceChartData}
              onSliceClick={(type) => fetchDeviceByStatus([type])}
              emptyLabel={localizationStrings?.noData || "No data"}
            />
          </NmsWidget>
        </div>

        <div key="alerts">
          <NmsWidget title={localizationStrings?.alerts || "Alerts"} count={alertTotal}>
            <AlertsChart
              loading={loading}
              faults={faults}
              formatSeverity={formatSeverityLevel}
              onSeverityClick={fetchAlertsBySeverity}
              emptyLabel={localizationStrings?.noData || "No data"}
            />
          </NmsWidget>
        </div>

        <div key="clients">
          <NmsWidget
            title={localizationStrings?.managedClients || "Managed clients"}
            count={wiredCount + wirelessCount}
          >
            <ClientsChart
              loading={loading}
              wiredCount={wiredCount}
              wirelessCount={wirelessCount}
              wiredLabel={localizationStrings?.wired || "Wired"}
              wirelessLabel={localizationStrings?.wireless || "Wireless"}
              emptyLabel={localizationStrings?.noData || "No data"}
              onBarClick={(key) => navigate("/client", { state: { filter: key } })}
            />
          </NmsWidget>
        </div>

        <div key="edges">
          <NmsWidget title={localizationStrings?.edges || "Edges"} count={edgeCounts.totalCount || 0}>
            <EdgesPanel
              loading={loading}
              edgeCounts={edgeCounts}
              labels={{
                active: localizationStrings?.active || "Active",
                inactive: localizationStrings?.inactive || "Inactive",
                managed: localizationStrings?.managedDevices || "Managed devices",
              }}
              emptyLabel={localizationStrings?.noData || "No data"}
              onActiveClick={() => handleEdgeCountClick("active")}
              onInactiveClick={() => handleEdgeCountClick("inactive")}
              onManagedClick={handleEdgeManagedCountClick}
            />
          </NmsWidget>
        </div>

        <div key="inventory">
          <NmsWidget
            title="Device inventory"
            extra={
              <button type="button" className="nms-link-btn" onClick={() => navigate("/device-listing")}>
                {localizationStrings?.devices || "Devices"}
              </button>
            }
          >
            <DeviceInventory
              loading={loading}
              devices={devices}
              emptyLabel={localizationStrings?.noData || "No data"}
              onRowClick={() => navigate("/device-listing")}
              onViewAll={() => navigate("/device-listing")}
              columns={{
                name: localizationStrings?.deviceName || "Device",
                type: localizationStrings?.deviceType || "Type",
                protocol: localizationStrings?.protocolType || "Protocol",
                status: localizationStrings?.health || "Health",
              }}
            />
          </NmsWidget>
        </div>

        <div key="trends">
          <NmsWidget title={localizationStrings?.deviceOnboarding || "Device onboarding"}>
            <OverviewTrends hideHeader />
          </NmsWidget>
        </div>

        {showHealth && (
          <div key="health">
            <NmsWidget
              title={localizationStrings?.systemHealth || "System health"}
              extra={
                <button type="button" className="nms-link-btn" onClick={() => navigate("/system-info")}>
                  {localizationStrings?.moreInfo || "More info"}
                </button>
              }
            >
              <OverviewSystemHealth hideHeader />
            </NmsWidget>
          </div>
        )}
      </ResponsiveGridLayout>
    </div>
  );
};

export default OverviewDashboard;
